import React, { createContext, useContext, useState, useEffect } from "react";
import {
  login,
  signup,
  refreshToken as refreshTokenAPI,
  AuthResponse,
  logout,
} from "../api/authService";

import {
  getToken,
  setToken,
  getUser,
  setUser,
  removeToken,
  removeUser,
  clearSurveyDrafts,
} from "../utils/storage";

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<any>;
  signup: (email: string, password: string) => Promise<any>;
  logout: () => void;
  updateUser: (user: any) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUserState] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getToken();
      if (token) {
        setIsAuthenticated(true);
        setUserState(getUser());
      } else {
        removeToken();
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const handleAuthResponse = (
    data: AuthResponse,
    rememberMe: boolean = false
  ) => {
    setToken(data.token, rememberMe);
    setUser(data?.user, rememberMe);
    setUserState(data?.user);
    setIsAuthenticated(true);
    sessionStorage.removeItem("hasSeenDriverWelcome");
    return {
      status: "success",
      message: "Operation successful",
      token: data.token,
      user: data.user,
    };
  };

  // Error handler for login/signup
  const handleAuthError = (error: any) => {
    const errorMessage = error.response?.data?.detail || "Operation failed";
    return { status: "error", message: errorMessage }; // Return error status and message
  };

  // Login handler
  const handleLogin = async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ): Promise<any> => {
    try {
      const data: AuthResponse = await login(email, password);
      return handleAuthResponse(data, rememberMe);
    } catch (error: any) {
      return handleAuthError(error);
    }
  };

  // Signup handler
  const handleSignup = async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ): Promise<any> => {
    try {
      const data: AuthResponse = await signup(email, password);
      return handleAuthResponse(data, rememberMe); // Pass rememberMe
    } catch (error: any) {
      return handleAuthError(error);
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      // const data: AuthResponse = await logout();
      removeToken(); // Clear tokens
      removeUser();
      clearSurveyDrafts();
      setUserState(null);
      setIsAuthenticated(false);
    } catch (error: any) {
      return handleAuthError(error);
    }
  };

  // Handle token refresh (optional: can be triggered by component or Axios interceptor)
  const handleTokenRefresh = async (): Promise<any> => {
    try {
      const data: AuthResponse = await refreshTokenAPI();
      return handleAuthResponse(data); // Handle success
    } catch (error: any) {
      handleLogout(); // Log out on refresh failure
      return handleAuthError(error); // Handle error
    }
  };

  const handleUpdateUser = (newUser: any) => {
    setUserState(newUser);
    setUser(newUser, true);
  };

  const contextValue = React.useMemo(() => ({
    isAuthenticated,
    user,
    login: handleLogin,
    signup: handleSignup,
    logout: handleLogout,
    updateUser: handleUpdateUser,
    loading,
  }), [isAuthenticated, user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to access the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
